import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { memo, useEffect, useRef, useState } from 'react';

import { db } from '../../lib/firebase';
import Styles from './Instrument.module.css';

const typingInField = (event) => {
  const el = event.target;
  return Boolean(
    el &&
      (el.tagName === 'INPUT' ||
        el.tagName === 'TEXTAREA' ||
        el.tagName === 'SELECT' ||
        el.isContentEditable),
  );
};

// Every modal sets aria-modal="true".
const modalIsOpen = () => Boolean(document.querySelector('[aria-modal="true"]'));

const Instrument = ({
  correctKeys,
  handleIncorrect,
  handleAnswer,
  instrument,
  handleLoading,
  isDisplay,
  isTimeUp,
  setIsTimeUp,
  setIsRunning,
  mode,
  // passive: just draw the valves and follow the keys. Nothing is graded, no
  // input is ever locked out and no hold timer runs, so a valve always goes
  // down the instant its key does. The Scales page judges on the beat itself.
  passive = false,
  // euphonium only: a fourth valve played with the 'f' key
  fourthValve = false,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isInputEnabled, setIsInputEnabled] = useState(true);
  const isInputEnabledRef = useRef(true);
  const holdTimerRef = useRef(null);
  const lastSubmitTimeRef = useRef(0);
  const spacebarEnabledRef = useRef(true);
  const spacebarTimerRef = useRef(null);
  const pressDuration = mode === 'Scales' ? 100 : 250;
  const passiveRef = useRef(passive);
  const fourthValveRef = useRef(fourthValve);

  useEffect(() => {
    passiveRef.current = passive;
  }, [passive]);

  // Turning the fourth valve off while 'f' is held must not leave it stuck down
  useEffect(() => {
    fourthValveRef.current = fourthValve;
    if (!fourthValve) setPressedKeys((prev) => (prev.includes('f') ? prev.filter((k) => k !== 'f') : prev));
  }, [fourthValve]);

  // A key released while the window wasn't focused never sends keyup, which
  // would leave a valve stuck down.
  useEffect(() => {
    if (!passive) return undefined;
    const release = () => setPressedKeys([]);
    window.addEventListener('blur', release);
    return () => window.removeEventListener('blur', release);
  }, [passive]);

  useEffect(() => {
    isInputEnabledRef.current = isInputEnabled;
  }, [isInputEnabled]);

  const getValveImagePath = (valveId, state) => {
    return require(`../../assets/valves/${valveId}-${state}.png`);
  };

  const getRotaryValveImagePath = (rotaryValveId, state) => {
    return require(`../../assets/rotary-valves/${rotaryValveId}-${state}.png`);
  };

  const getSlideImagePath = (slideId, state) => {
    if (state === 1) require(`../../assets/slides/${slideId}1.png`);
    for (let i = 1; i <= 7; i++) {
      if (pressedKeys.includes(i.toString())) {
        return require(`../../assets/slides/${slideId}${i}.png`);
      }
    }
    return require(`../../assets/slides/${slideId}1.png`);
  };

  const handleSlideImage = () => {
    return slides.some((slide) => pressedKeys.includes(slide));
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const loadUserData = async () => {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          const data = userDoc.data().selectedItems;
          if (data) {
            setSelectedItems({
              valvedInstruments: data.valvedInstruments,
              valveFelts: data.valveFelts,
              inlays: data.inlays,
              rotaryValves: data.rotaryValves,
              slides: data.slides,
            });
          }
        };
        loadUserData();
      }
    });

    return () => unsubscribe();
  }, []);

  const [pressedKeys, setPressedKeys] = useState([]);
  const pressedKeysRef = useRef(pressedKeys);

  const [selectedItems, setSelectedItems] = useState({
    valvedInstruments: 0,
    valveFelts: 0,
    inlays: 0,
    rotaryValves: 0,
    slides: 0,
  });

  const correctKeysRef = useRef(correctKeys);
  const instrumentRef = useRef(instrument);
  const handleIncorrectRef = useRef(handleIncorrect);
  const handleAnswerRef = useRef(handleAnswer);

  useEffect(() => {
    pressedKeysRef.current = pressedKeys;
  }, [pressedKeys]);

  useEffect(() => {
    correctKeysRef.current = correctKeys;
  }, [correctKeys]);

  useEffect(() => {
    instrumentRef.current = instrument;
  }, [instrument]);

  useEffect(() => {
    handleIncorrectRef.current = handleIncorrect;
  }, [handleIncorrect]);

  useEffect(() => {
    handleAnswerRef.current = handleAnswer;
  }, [handleAnswer]);

  useEffect(() => {
    if (selectedItems) {
      const valveId =
        '' +
        selectedItems.valvedInstruments +
        selectedItems.valveFelts +
        selectedItems.inlays;

      if (selectedItems.valvedInstruments === 3) {
        setValveOpened(require(`../../assets/valves/mushroom-opened.png`));
        setValveClosed(require(`../../assets/valves/mushroom-closed.png`));
      } else if (selectedItems.valvedInstruments === 4) {
        setValveOpened(require(`../../assets/valves/rifle-opened.gif`));
        setValveClosed(require(`../../assets/valves/rifle-closed.png`));
      } else {
        setValveOpened(getValveImagePath(valveId, 'opened'));
        setValveClosed(getValveImagePath(valveId, 'closed'));
      }

      const rotaryValveId = '' + selectedItems.rotaryValves;
      if (selectedItems.rotaryValves === 2) {
        setRotaryValveOpened(
          require(`../../assets/rotary-valves/spork-opened.png`),
        );
        setRotaryValveClosed(
          require(`../../assets/rotary-valves/spork-closed.png`),
        );
      } else {
        setRotaryValveOpened(getRotaryValveImagePath(rotaryValveId, 'opened'));
        setRotaryValveClosed(getRotaryValveImagePath(rotaryValveId, 'closed'));
      }

      setSlideId('' + selectedItems.slides);

      const timer = setTimeout(() => {
        setIsLoading(false);
        handleLoading(false);
      }, 300);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [selectedItems]);

  const [valveOpened, setValveOpened] = useState('');
  const [valveClosed, setValveClosed] = useState('');
  const [rotaryValveOpened, setRotaryValveOpened] = useState('');
  const [rotaryValveClosed, setRotaryValveClosed] = useState('');
  const slides = ['1', '2', '3', '4', '5', '6', '7'];
  const [slideId, setSlideId] = useState('');

  // Decode every valve picture before it's needed, so a valve doesn't wait on
  // an image to load the first time it goes down.
  useEffect(() => {
    [valveOpened, valveClosed, rotaryValveOpened, rotaryValveClosed].forEach((src) => {
      if (!src) return;
      const image = new Image();
      image.src = src;
      if (image.decode) image.decode().catch(() => {});
    });
    if (slideId) {
      for (let n = 1; n <= 7; n += 1) {
        try {
          const image = new Image();
          image.src = require(`../../assets/slides/${slideId}${n}.png`);
        } catch (e) {
          // that slide position has no picture
        }
      }
    }
  }, [valveOpened, valveClosed, rotaryValveOpened, rotaryValveClosed, slideId]);

  const attemptSubmit = (candidateKeys) => {
    if (passiveRef.current) return;
    if (!isInputEnabledRef.current) return;

    // Prevent double submissions within 300ms
    const now = Date.now();
    if (now - lastSubmitTimeRef.current < 300) return;
    lastSubmitTimeRef.current = now;

    if (candidateKeys.length === 0) return;

    const currentCorrect = correctKeysRef.current || [];
    let works = false;
    for (let i = 0; i < currentCorrect.length; i++) {
      if (
        JSON.stringify([...candidateKeys].sort()) ===
        JSON.stringify([...currentCorrect[i]].sort())
      ) {
        works = true;
        break;
      }
    }

    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }

    // Disable spacebar for 300ms after any submission
    spacebarEnabledRef.current = false;
    if (spacebarTimerRef.current) {
      clearTimeout(spacebarTimerRef.current);
    }
    spacebarTimerRef.current = setTimeout(() => {
      spacebarEnabledRef.current = true;
    }, 300);

    if (works) {
      handleAnswerRef.current(true);
    } else {
      setIsInputEnabled(false);
      isInputEnabledRef.current = false;
      handleIncorrectRef.current(true, candidateKeys);

      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current);
        holdTimerRef.current = null;
      }

      setTimeout(() => {
        setIsInputEnabled(true);
        isInputEnabledRef.current = true;
        setPressedKeys([]);
        handleIncorrectRef.current(false);
        handleAnswerRef.current(false);
      }, 2000);
    }
  };

  const isPlayKey = (key) =>
    ['j', 'k', 'l', 'q', 'w', 'e', 'c', ';'].includes(key) ||
    slides.includes(key) ||
    (key === 'f' && fourthValveRef.current);

  const handleKeyDown = (event) => {
    if (typingInField(event) || modalIsOpen()) return;
    if (!passiveRef.current && !isInputEnabledRef.current) return;
    const key = event.key;
    if (isPlayKey(key)) {
      setPressedKeys((prevKeys) => {
        if (prevKeys.includes(key)) return prevKeys;
        const newKeys = [...prevKeys, key];
        if (holdTimerRef.current) {
          clearTimeout(holdTimerRef.current);
        }
        if (!passiveRef.current && !(newKeys.length === 0)) {
          holdTimerRef.current = setTimeout(
            () => attemptSubmit(newKeys),
            pressDuration,
          );
        }
        return newKeys;
      });
    }
  };

  const handleKeyUp = (event) => {
    if (!passiveRef.current && !isInputEnabledRef.current) return;
    const key = event.key;
    if (isPlayKey(key)) {
      setPressedKeys((prevKeys) => {
        const newKeys = prevKeys.filter((v) => v !== key);
        if (holdTimerRef.current) {
          clearTimeout(holdTimerRef.current);
        }
        if (!passiveRef.current && !(newKeys.length === 0)) {
          holdTimerRef.current = setTimeout(
            () => attemptSubmit(newKeys),
            pressDuration,
          );
        }
        return newKeys;
      });
    }
  };

  const handleSpaceBar = (event) => {
    if (typingInField(event) || modalIsOpen()) return;
    if (passiveRef.current) return;
    if (!isInputEnabledRef.current) return;
    if (!spacebarEnabledRef.current) return; // Block spacebar during cooldown

    const key = event.key;
    if (key === ' ') {
      // Prevent double submissions
      const now = Date.now();
      if (now - lastSubmitTimeRef.current < 300) return;
      lastSubmitTimeRef.current = now;

      let works = false;
      const currentPressed = pressedKeysRef.current || [];
      const currentCorrect = correctKeysRef.current || [];

      if (
        instrumentRef.current === 'trombone' &&
        currentPressed.length === 0 &&
        currentCorrect.some(
          (arr) => JSON.stringify(arr) === JSON.stringify(['1']),
        )
      ) {
        works = true;
      } else {
        for (let i = 0; i < currentCorrect.length; i++) {
          if (
            JSON.stringify([...currentPressed].sort()) ===
            JSON.stringify([...currentCorrect[i]].sort())
          ) {
            works = true;
            break;
          }
        }
      }

      // Disable spacebar for 300ms
      spacebarEnabledRef.current = false;
      if (spacebarTimerRef.current) {
        clearTimeout(spacebarTimerRef.current);
      }
      spacebarTimerRef.current = setTimeout(() => {
        spacebarEnabledRef.current = true;
      }, 300);

      if (works) {
        handleAnswerRef.current(true);
      } else {
        setIsInputEnabled(false);
        isInputEnabledRef.current = false;
        handleIncorrectRef.current(true, currentPressed);

        setTimeout(() => {
          setIsInputEnabled(true);
          isInputEnabledRef.current = true;
          setPressedKeys([]);
          handleIncorrectRef.current(false);
          handleAnswerRef.current(false);
        }, 2000);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('keydown', handleSpaceBar);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('keydown', handleSpaceBar);
      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current);
        holdTimerRef.current = null;
      }
      if (spacebarTimerRef.current) {
        clearTimeout(spacebarTimerRef.current);
        spacebarTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!isTimeUp) return;
    setIsInputEnabled(false);
    handleIncorrectRef.current(true, pressedKeysRef.current || []);

    const t = setTimeout(() => {
      setIsInputEnabled(true);
      setPressedKeys([]);
      handleIncorrectRef.current(false);
      handleAnswerRef.current(false);
      setIsTimeUp(false);
      setIsRunning(true);
      window.addEventListener('keydown', handleSpaceBar);
    }, 2000);

    return () => clearTimeout(t);
  }, [isTimeUp]);

  return (
    <div>
      {!isLoading && (
        <div className={Styles.instrumentContainer}>
          {instrument === 'frenchHorn' ? (
            <div className={Styles.frenchHorn}>
              {pressedKeys.includes('q') ? (
                <img src={rotaryValveClosed} alt={'Closed rotary valve'} />
              ) : (
                <img src={rotaryValveOpened} alt={'Opened rotary valve'} />
              )}
              {pressedKeys.includes('w') ? (
                <img src={rotaryValveClosed} alt={'Closed rotary valve'} />
              ) : (
                <img src={rotaryValveOpened} alt={'Opened rotary valve'} />
              )}
              {pressedKeys.includes('e') ? (
                <img src={rotaryValveClosed} alt={'Closed rotary valve'} />
              ) : (
                <img src={rotaryValveOpened} alt={'Opened rotary valve'} />
              )}
              {pressedKeys.includes('c') ? (
                <img
                  className={Styles.trigger}
                  src={rotaryValveClosed}
                  alt={'Closed rotary valve'}
                />
              ) : (
                <img
                  className={Styles.trigger}
                  src={rotaryValveOpened}
                  alt={'Opened rotary valve'}
                />
              )}
            </div>
          ) : instrument === 'trombone' ? (
            <div className={Styles.slide}>
              {handleSlideImage() ? (
                <img
                  src={getSlideImagePath(slideId, 0)}
                  alt={'Slide position'}
                />
              ) : (
                <img
                  src={getSlideImagePath(slideId, 1)}
                  alt={'First position slide'}
                />
              )}
            </div>
          ) : instrument === 'tuba' ? (
            <div className={Styles.tuba}>
              {pressedKeys.includes('j') ? (
                <img src={rotaryValveClosed} alt={'Closed rotary valve'} />
              ) : (
                <img src={rotaryValveOpened} alt={'Opened rotary valve'} />
              )}
              {pressedKeys.includes('k') ? (
                <img src={rotaryValveClosed} alt={'Closed rotary valve'} />
              ) : (
                <img src={rotaryValveOpened} alt={'Opened rotary valve'} />
              )}
              {pressedKeys.includes('l') ? (
                <img src={rotaryValveClosed} alt={'Closed rotary valve'} />
              ) : (
                <img src={rotaryValveOpened} alt={'Opened rotary valve'} />
              )}
              {pressedKeys.includes(';') ? (
                <img src={rotaryValveClosed} alt={'Closed rotary valve'} />
              ) : (
                <img src={rotaryValveOpened} alt={'Opened rotary valve'} />
              )}
            </div>
          ) : (
            <div className={Styles.valves}>
              {pressedKeys.includes('j') ? (
                <img src={valveClosed} alt={'Closed valve'} />
              ) : (
                <img src={valveOpened} alt={'Opened valve'} />
              )}
              {pressedKeys.includes('k') ? (
                <img src={valveClosed} alt={'Closed valve'} />
              ) : (
                <img src={valveOpened} alt={'Opened valve'} />
              )}
              {pressedKeys.includes('l') ? (
                <img src={valveClosed} alt={'Closed valve'} />
              ) : (
                <img src={valveOpened} alt={'Opened valve'} />
              )}
              {instrument === 'euphonium' && fourthValve && (
                <img
                  className={Styles.fourth}
                  src={pressedKeys.includes('f') ? valveClosed : valveOpened}
                  alt={pressedKeys.includes('f') ? 'Closed fourth valve' : 'Opened fourth valve'}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// memo: the Scales page re-renders every beat; the valves only need to redraw
// when a key or a prop actually changes.
export default memo(Instrument);